/**
 * @file FormDialog.tsx
 * @brief Shared chrome for create/edit centered form modals.
 *
 * @details
 * Sticky header/footer, scroll body, and section cards. Right-sheet
 * create/edit (Users / Employees) uses `FormSheet.tsx` instead.
 *
 * @author DES
 * @date 28-07-2026
 * @updated 29-07-2026 — note FormSheet sibling for flush sheet forms
 */

import {
  BARE_FIELDS,
  FieldStyleContext,
  bareCaptionClass,
  bareCaptionErrorClass,
  bareDescendantsClass,
  bareFieldLineClass,
  fieldErrorText,
} from "@/components/ui/fieldStyle";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Label } from "@/components/ui/Label";
import { cn } from "@/shared/theme/cn";

export type FormDialogMaxWidth = "md" | "lg" | "xl" | "2xl" | "3xl";

const MAX_WIDTH: Record<FormDialogMaxWidth, string> = {
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
};

export const FormDialogContent = forwardRef<
  ElementRef<typeof DialogContent>,
  ComponentPropsWithoutRef<typeof DialogContent> & {
    maxWidth?: FormDialogMaxWidth;
  }
>(({ className, maxWidth = "lg", ...props }, ref) => (
  <DialogContent
    ref={ref}
    className={cn(
      "flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0",
      MAX_WIDTH[maxWidth],
      className,
    )}
    {...props}
  />
));
FormDialogContent.displayName = "FormDialogContent";

export function FormDialogHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogHeader
      className={cn(
        "shrink-0 space-y-1 border-b border-border px-6 py-4 pr-12 text-left",
        className,
      )}
      {...props}
    />
  );
}

export function FormDialogBody({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4",
        className,
      )}
      {...props}
    />
  );
}

export function FormDialogFooter({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogFooter>) {
  return (
    <DialogFooter
      className={cn(
        "shrink-0 border-t border-border px-6 py-4 sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

export { DialogTitle as FormDialogTitle, DialogDescription as FormDialogDescription };

export function FormSection({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "overflow-hidden rounded-lg border border-border bg-card",
        className,
      )}
    >
      <header className="border-b border-border bg-muted/40 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {subtitle}
          </p>
        ) : null}
      </header>
      <div className="space-y-3 px-4 py-3">{children}</div>
    </section>
  );
}

export function FormFieldGroup({
  label,
  hint,
  error,
  required,
  htmlFor,
  icon,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  /** Bare only: a leading brand icon, as the phone's `bareShowsIcon`
   *  (Profile, Company profile). The caption, hint and hairline start
   *  after it. */
  icon?: ReactNode;
  children: ReactNode;
}) {
  // Bare validation is the phone's (fieldStyle.tsx `fieldErrorText`): the
  // error takes the label's place in red, no line under the field.
  const bareError = BARE_FIELDS && error ? fieldErrorText(label, error) : undefined;
  if (BARE_FIELDS && icon) {
    return (
      <FieldStyleContext.Provider value={{ bare: true, label, error: bareError }}>
        <div className={cn("group/field relative pb-2.5", bareDescendantsClass)}>
          <Label
            htmlFor={htmlFor}
            className={cn(
              bareCaptionClass,
              "flex items-center gap-1 pl-8 group-has-[input:placeholder-shown]/field:invisible",
              bareError && bareCaptionErrorClass,
            )}
          >
            <span className="min-w-0 truncate">{bareError ?? label}</span>
            {required ? <span className="text-destructive">*</span> : null}
          </Label>
          <div className="flex items-center gap-3">
            <span className="flex w-5 shrink-0 items-center justify-center text-brand [&_svg]:h-5 [&_svg]:w-5">
              {icon}
            </span>
            <div className="min-w-0 flex-1">{children}</div>
          </div>
          {!bareError && hint ? (
            <p className="mt-1 pl-8 text-xs text-muted-foreground">{hint}</p>
          ) : null}
          <span className="absolute bottom-0 left-8 right-0 h-px bg-app-text/10" aria-hidden />
        </div>
      </FieldStyleContext.Provider>
    );
  }
  if (BARE_FIELDS) {
    // The app's bare field (DES 01-10-2026, fieldStyle.tsx): the label is the
    // placeholder, and a 13px caption once the input has a value. The caption
    // keeps its space while empty (invisible, like the phone's opacity 0) so
    // rows never jump.
    return (
      <FieldStyleContext.Provider value={{ bare: true, label, error: bareError }}>
        <div className={cn("group/field", bareFieldLineClass, bareDescendantsClass)}>
          <Label
            htmlFor={htmlFor}
            className={cn(
              bareCaptionClass,
              "flex items-center gap-1 group-has-[input:placeholder-shown]/field:invisible",
              bareError && bareCaptionErrorClass,
            )}
          >
            <span className="min-w-0 truncate">{bareError ?? label}</span>
            {required ? <span className="text-destructive">*</span> : null}
          </Label>
          {children}
          {!bareError && hint ? (
            <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
          ) : null}
        </div>
      </FieldStyleContext.Provider>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="flex items-center gap-1">
        {label}
        {required ? <span className="text-destructive">*</span> : null}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
